import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createCookie, readCookie } from '@wbk/utils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { defaultLang, languages } from '@/i18n/config';
import Context, { FALLBACK_CURRENCY } from './context';

type Props = {
  children: React.ReactNode;
};

const LocalizationProvider = ({ children }: Props) => {
  const { i18n } = useTranslation('common');
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const validLang = lang && languages.includes(lang) ? lang : defaultLang;
  const navigate = useNavigate();
  const currentCurrency = readCookie('currency') || FALLBACK_CURRENCY;

  useEffect(() => {
    if (validLang) {
      setLocale(validLang);
    }
    // eslint-disable-next-line
  }, []);

  const setLocale = async (language: string) => {
    const pathWithoutLang = pathname.replace(`/${lang}`, '');
    await i18n.changeLanguage(language);
    createCookie({ name: 'lang', value: language, expiration: 30 });
    document.documentElement.lang = language;
    document.documentElement.dir = language.startsWith('ar') ? 'rtl' : 'ltr';

    const search = new URLSearchParams(window.location.search).toString();
    const dest = `/${language}${pathWithoutLang}${search ? `?${search}` : ''}`;
    navigate(dest);
  };

  return (
    <Context.Provider
      value={{
        locale: validLang,
        setLocale,
        currentCurrency,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default LocalizationProvider;
